import { Box, Button } from '@mui/material';
import { DialogName } from 'app/enums';
import { modalsReducers } from 'app/store/modal/slice';
import Logo from 'assets/images/okletsplay_logo-text.png';
import classNames from 'classnames';
import { FreeWinResult } from 'modules/matches/components/WinningAnimationDialog/FreeWinResult';
import { PrizeCountUp } from 'modules/matches/components/WinningAnimationDialog/PrizeCountUp';
import { WinningAnimation } from 'modules/matches/components/WinningAnimationDialog/WinningAnimation';
import React, { FC, useCallback, useContext, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogProps } from 'shared/components/Dialog';
import { withModalOpenChecker } from 'shared/HOC/withModalOpenChecker';
import { useStyles } from './WinningAnimationDialog.styles';
import { useBeforeUnload } from 'shared/hooks';
import { ActivityDetectorContext } from 'app/providers';
import { PrizeItem } from './PrizeCountUp';
import { walletSelector } from 'modules/payments/store/wallet/selectors';
import { convertCryptoToUsd } from 'shared/utils';
import { CurrencyType } from 'modules/payments/constants';
import { MultiCurrencyConfig } from 'config/multi-currency-config';
import { formatToFixed } from '@okletsplay/core';
import { GameActivityType } from 'shared/enums';

type WinningAnimationDialogProps = Partial<DialogProps> & {
  prize?: number;
  activity: GameActivityType;
};

const WinningAnimationDialogComponent: FC<WinningAnimationDialogProps> = ({
  prize,
  onClose,
  activity,
}) => {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const [isVideoEnded, setIsVideoEnded] = useState(false);
  const [isResultAvailable, setIsResultAvailable] = useState(false);
  const { isUserActive } = useContext(ActivityDetectorContext);

  const exchangeRate = useSelector(walletSelector.exchangeRate);
  const prizeItems: PrizeItem[] = useMemo(() => {
    if (
      (activity === GameActivityType.Match && MultiCurrencyConfig.isEnabledForMatches()) ||
      (activity === GameActivityType.Tournament && MultiCurrencyConfig.isEnabledForTournaments())
    ) {
      return [
        {
          amount: formatToFixed(prize ?? 0),
          currency: CurrencyType.CryptoPrimary,
        },
      ];
    }

    return [
      {
        amount: prize ?? 0,
        currency: CurrencyType.CryptoSecondary,
      },
      {
        amount: convertCryptoToUsd(prize ?? 0, exchangeRate),
        currency: CurrencyType.Usd,
      },
    ];
  }, [activity, exchangeRate, prize]);

  const hasPrize = prizeItems.some((prizeItem) => Boolean(prizeItem.amount));

  const closeDialogHandler = useCallback(() => {
    onClose?.();

    dispatch(modalsReducers.closeModal(DialogName.WinningAnimation));
    setIsVideoEnded(false);
    setIsResultAvailable(false);
  }, [onClose, dispatch]);

  useBeforeUnload(onClose);

  if (!isUserActive) {
    return null;
  }

  return (
    <Dialog
      maxWidth="sm"
      onClose={closeDialogHandler}
      contentHeight={380}
      name={DialogName.WinningAnimation}
      actions={
        <Button
          fullWidth
          className={classNames(classes.button, { [classes.buttonVisible]: isVideoEnded })}
          size="medium"
          color="primary"
          variant="outlined"
          onClick={closeDialogHandler}
        >
          Hell Yeah!
        </Button>
      }
    >
      <Box className={classes.root}>
        <Box className={classes.titleLogo}>
          <img src={Logo} alt="logo" />
        </Box>
        {hasPrize ? (
          <Box className={classes.countUp}>
            {prizeItems.map((prizeItem) => (
              <PrizeCountUp
                prize={prizeItem}
                isResultAvailable={isResultAvailable}
                key={prizeItem.currency}
              />
            ))}
          </Box>
        ) : (
          <FreeWinResult isVideoEnded={isResultAvailable} />
        )}
        <WinningAnimation
          withPrize={hasPrize}
          onEnded={() => setIsVideoEnded(true)}
          onShowResult={() => setIsResultAvailable(true)}
        />
      </Box>
    </Dialog>
  );
};

export const WinningAnimationDialog = withModalOpenChecker(
  WinningAnimationDialogComponent,
  DialogName.WinningAnimation
);
